import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import Button from '@/components/ui/button'
import { convertFromCanonical, convertToCanonical, parseCurrencyParameter } from '@/util/currencies'
import Currency from '@/components/currency/Currency'
import wagerService from '@/services/wager'
import { i18n } from '@/locale/i18n'
import mixpanelService from '@/services/analytics/Mixpanel'
import { CLICK_COMPONENT, CLICK_ELEMENT, CLICK_SOURCE } from '@/constants/analyticsClick'
import { formatNumber, normalizeNumericStr } from '@/util/localizeNumber'
import { useActiveCurrency, useCurrencies, useCurrentUser, useUserBalance, useOpenLogin } from '@/hooks'
import Dropdown from '@/components/ui/dropdown'
import Thumbnail from '@/components/ui/thumbnail'
import Image from '@/components/ui/image/Image'
import constants from '@/constants'
import { useDispatch, useSelector } from 'react-redux'
import {
  resetCurrentUser,
  selectCurrentUserStatus,
  selectUserFromAllowedCountry,
  updateCurrentUserSettings,
} from '@/store/account'
import { fetchUserOrders, placeBet, placeLimitOrder, selectLastBetStatus, selectLastOrderStatus } from '@/store/wagers'
import { shortenText } from '@/util/textFormat'
import {
  selectActiveLanguage,
  selectAllExchangeRate,
  selectExchangeRate,
  selectExchangeRateStatus,
  selectExchangeRateUpdateCode,
  selectPageLayout,
  selectSelectedLimitPrice,
  setSelectedLimitPrice,
  setLimitOrderFillPreview,
  clearLimitOrderFillPreview,
} from '@/store/app'
import { toastService } from '@/api/toast'
import { buildErrorToast } from '@/util/errorMessages'
import { useModal } from '@/components/modal-provider/ModalProvider'
import EmailNotConfirmed from '@/components/modal-provider/global-modal/email-not-confirmed'
import { useAutoAnimate } from '@formkit/auto-animate/react'
import Calendar from '@/components/ui/Calendar'
import CalendarSheet from '@/components/ui/Calendar/CalendarSheet'
import PlaceBetInput from '../place-bet-input/PlaceBetInput'
import useIsMobileSize from '@/hooks/useIsMobileSize'
import { useNavigate } from 'react-router-dom'
import { depositUrl } from '@/urls'
import { selectOrderBookByOutcomeId, fetchOrderBook } from '@/store/markets'
import { convertUrl, marketMakerDocumentationUrl } from '@/urls/index'
import { CheckIcon, InfoIcon } from '@/assets/icons'
import ArrowUp from '@/assets/icons/arrowUp'
import ArrowDown from '@/assets/icons/arrowDown'
import OverlayPanel from '@/components/OverlayPanel/OverlayPanel'
import { getErrorInfo, handleOrderError } from '@/store/wagers'
import { getPositionLabels } from '@/features/market/utils/positionLabels'
import { deriveMarketUi, getVirtualOutcomesForMonoCustom } from '@/features/market/utils/marketType'
import { getSportsEventData } from '@/features/market/utils/sportsEvent'
import { getSportOutcomeLabel, resolveSportOutcomeSlots } from '@/features/market/utils/sportOutcomeSlots'
import { getTeamFlagUrl } from '@/features/market/utils/teamFlag'
import {
  calculateFeesForShares,
  computeMatchableShares,
  simulateOrderBookBuyByAmount,
  simulateOrderBookBuyByShares,
  computeLimitBuyFillPreview,
  computeMakerRebate,
} from '@/features/market/utils/orderBookCalculations'

const prefetchImage = (src) => {
  if (!src) return
  try {
    const img = new Image()
    img.decoding = 'async'
    img.src = src
  } catch {
    // ignore prefetch errors (e.g. SSR / older browsers)
  }
}

const BuyPlaceBet = forwardRef(
  (
    {
      question,
      outcome,
      onToggle,
      quickbet = false,
      onChangeOutcome = () => { },
      place,
      setBetPlaced,
      onWagerAction,
      position,
      setPosition = () => { },
      marketType = 'market',
      onChangeToLimitOrder = () => { },
      onOrderConflict = () => { },
    },
    ref
  ) => {
    const currencies = useCurrencies().currencies
    const currencyId = useActiveCurrency().id
    const currency = currencies[currencyId]
    const lastBetStatus = useSelector(selectLastBetStatus())
    const lastOrderStatus = useSelector(selectLastOrderStatus())
    const orderBook = useSelector(selectOrderBookByOutcomeId(outcome?.id, currencyId, position))
    const exchangeRate = useSelector(
      selectExchangeRate(
        currency.id === constants.REAL_MONEY_PLACEHOLDER ? 'USDC' : currencyId,
        question?.canonical_currency
      )
    )
    const exchangeRateStatus = useSelector(selectExchangeRateStatus())
    const { adminMode } = useSelector(selectPageLayout)
    const locale = useSelector(selectActiveLanguage()) || 'en'
    const fromAllowedCountry = useSelector(selectUserFromAllowedCountry())
    const exchangeRateAll = useSelector(selectAllExchangeRate())

    const userBalance = useUserBalance()
    const currentUserStatus = useSelector(selectCurrentUserStatus)
    const isMobileSize = useIsMobileSize()
    const navigate = useNavigate()
    const { openLogin } = useOpenLogin()
    const [animationParent] = useAutoAnimate()

    const textField = useRef()
    const dispatch = useDispatch()
    const user = useCurrentUser()
    const { showModal } = useModal()
    const parsedCurrencyId = parseCurrencyParameter(currencyId)
    // Track if we've initialized the value for the current currency/question
    const initializedKey = useRef(null)

    const parsedDecimalPlaces =
      currencies[currencyId]?.display_decimal_places - Math.abs(Math.log10(currencies[currencyId].display_ratio))
    const maxDecimalPlaces = Math.min(currency.display_decimal_places, parsedDecimalPlaces)
    const isSaving = lastBetStatus === constants.LOADING || lastOrderStatus === constants.LOADING
    const isRealMoney = currencyId !== currencies.OOM.id
    const NotEnoughFunds = useMemo(
      () => isRealMoney && userBalance < currency.min_bet_amount,
      [isRealMoney, userBalance, currency.min_bet_amount]
    )

    // Stabilize orderBook.buySeries to prevent infinite re-renders
    const stableOrderBookSeries = useMemo(() => {
      if (!orderBook?.buySeries) return null
      return orderBook.buySeries
    }, [orderBook?.buySeries?.length, JSON.stringify(orderBook?.buySeries)])

    // states
    const [value, setValue] = useState('')

    // Limit order specific states
    const [limitPrice, setLimitPrice] = useState('')
    const [shares, setShares] = useState('')
    const [expirationOption, setExpirationOption] = useState('until_cancelled')
    const [calendarDate, setCalendarDate] = useState({})
    const [calendarOpen, setCalendarOpen] = useState(false)

    const [errorId, setErrorId] = useState()
    const [warningId, setWarningId] = useState()
    const [outcomeTotalShares, setOutcomeTotalShares] = useState() // shares in ooms or btc
    const [pricePerShare, setPricePerShare] = useState()
    const [closestValidOrder, setClosestValidOrder] = useState()
    const [showSuccessCheck, setShowSuccessCheck] = useState(false)
    const [matchedShares, setMatchedShares] = useState(null) // shares that will be matched at limit price
    const prevIsSaving = useRef(false)
    const outcomePrice = useRef(outcome ? { id: outcome.id, price: outcome.price[parsedCurrencyId] } : null)
    const exchangeRateUsdc = useSelector(selectExchangeRate(currencies.USDC && currencies.USDC.id))
    const exchangeUpdateCode = useSelector(selectExchangeRateUpdateCode(currencyId))
    const isOrderBookAvailable =
      question?.order_book_enabled && orderBook && orderBook.buySeries && orderBook.buySeries.length > 0

    const pricePerShareRounded = outcome ? wagerService.roundPrice(Math.max(pricePerShare, 0.01), 2) : 0

    // Fetch sets status to LOADING but keeps prior ask/bid; only block submit on first load (no cached book).
    const orderBookHasCachedBuyLiquidity =
      question?.order_book_enabled && (orderBook?.buySeries?.length ?? 0) > 0
    const isOrderBookReadyForSubmit =
      !question?.order_book_enabled ||
      orderBook?.status === constants.COMPLETE ||
      (orderBook?.status === constants.LOADING && orderBookHasCachedBuyLiquidity)

    // Disable bet button until order book / price calculation is ready so user cannot submit with stale or missing data
    const isCalculationReady =
      outcome &&
      (marketType === 'market'
        ? question?.order_book_enabled
          ? isOrderBookReadyForSubmit &&
          (orderBook?.buySeries?.length > 0 ? (pricePerShare != null && pricePerShare > 0) : false)
          : pricePerShare != null && pricePerShare > 0
        : marketType === 'limit'
          ? isOrderBookReadyForSubmit &&
          (stableOrderBookSeries?.length > 0) &&
          limitPrice &&
          shares
          : false)

    // Get the maximum amount from orderbook for error display
    const maxOrderbookAmount =
      isOrderBookAvailable && orderBook?.buySeries?.length > 0
        ? Math.max(...orderBook.buySeries.map(order => order.cumulative_amount))
        : 0

    const maxOrderbookAmountFormatted = maxOrderbookAmount
      ? convertFromCanonical(maxOrderbookAmount, exchangeRate) / currency.display_ratio
      : 0

    const errorTypes = {
      outcome_not_selected: i18n('place_bet.outcome_not_selected'),
      // amount_beyond_max: (
      //   <React.Fragment>
      //     {i18n('place_bet.amount_beyond_max')}
      //     {maxTransactionPriceFormatted}
      //   </React.Fragment>
      // ),
      amount_below_min: (
        <>
          {i18n('place_bet.amount_below_min')}
          {`${formatNumber(currency.min_bet_amount, {
            locale,
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
            roundingMode: 'floor',
          })} ${currencies[currencyId].display_code}`}
        </>
      ),
      insufficient_funds: i18n('place_bet.insufficient_funds'),
      from_forbidden_country: i18n('place_bet.from_forbidden_country'),
      max_one_price_per_share: i18n('place_bet.max_one_price_per_share', {
        max: formatNumber(closestValidOrder?.cumulative_amount, {
          locale,
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
          roundingMode: 'floor',
        }),
      }),
      invalid_number: i18n('place_bet.invalid_number'),
      limit_price_too_high: i18n('place_bet.limit_price_too_high'),
      invalid_shares: i18n('place_bet.invalid_shares'),
      shares_required: i18n('place_bet.shares_required'),
      no_orderbook_orders: i18n('place_bet.no_orders_available'),
      amount_beyond_orderbook_max: i18n('place_bet.amount_beyond_orderbook_max', {
        max: maxOrderbookAmountFormatted
          ? formatNumber(maxOrderbookAmountFormatted, {
            locale,
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
            roundingMode: 'floor',
          })
          : '',
      }),
    }
    const warningTypes = {
      convert_disclamer: i18n('place_bet.convert_disclamer'),
    }

    const selectedLimitPrice = useSelector(selectSelectedLimitPrice)

    // Add isOrderBookAvailable state
    // Note: This function should not depend on orderbook data to avoid resetting on updates
    const getInitialPrice = useCallback(() => {
      const defaultAmount = wagerService.floorPrice(
        currency.default_bet_amount / currency.display_ratio,
        maxDecimalPlaces
      )

      // Balance may still be loading (null). Math.min(n, null) coerces null to 0 — never cap until loaded.
      if (userBalance == null) {
        return defaultAmount
      }

      const _userBalance = currencyId === 'BTC' ? userBalance * 1000 : userBalance

      // Note: We don't include orderbook max here to avoid resetting on orderbook updates
      // The orderbook limit is handled in validation, not initial value
      return Math.min(defaultAmount, _userBalance)
    }, [currency.default_bet_amount, currency.display_ratio, maxDecimalPlaces, currencyId, userBalance])

    useImperativeHandle(ref, () => ({}))

    useEffect(() => {
      if (currencyId === constants.REAL_MONEY_PLACEHOLDER) {
        setErrorId('')
        return
      }

      // Order-book and minimum-amount checks run for everyone; balance checks need a logged-in user
      const canCheckBalance = Boolean(user)
      let newErrorId = ''
      const unFormatedvalue = normalizeNumericStr(value + '', '.')
      const _userBalance = currencyId === 'BTC' ? userBalance * 1000 : userBalance

      // Check for minimum amount first (applies to all market types)
      if (marketType === 'market' && value && Number(unFormatedvalue) < currency.min_bet_amount) {
        newErrorId = 'amount_below_min'
      }

      // Check orderbook maximum for market orders
      if (marketType === 'market' && !newErrorId) {
        const maxOrderbookAmount =
          isOrderBookAvailable && stableOrderBookSeries?.length > 0
            ? Math.max(...stableOrderBookSeries.map(order => order.cumulative_amount))
            : null

        const canonicalValue = convertToCanonical(Number(unFormatedvalue) * currency.display_ratio, exchangeRate)

        if (question?.order_book_enabled && stableOrderBookSeries !== null) {
          if (stableOrderBookSeries.length === 0 && orderBook.status === constants.COMPLETE) {
            newErrorId = 'no_orderbook_orders'
          } else if (maxOrderbookAmount && canonicalValue > maxOrderbookAmount) {
            newErrorId = 'amount_beyond_orderbook_max'
          }
        }
        if (
          canCheckBalance &&
          !newErrorId &&
          ((marketType === 'market' && Number(unFormatedvalue) > _userBalance) || NotEnoughFunds)
        ) {
          newErrorId = 'insufficient_funds'
        }
      }

      // Check for limit order validations
      if (marketType === 'limit' && !newErrorId) {
        const maxCost = parseFloat(limitPrice) * parseFloat(shares)
        if (limitPrice && shares) {
          if (maxCost < currency.min_bet_amount || shares <= 0) {
            newErrorId = 'amount_below_min'
          } else if (canCheckBalance && maxCost > _userBalance) {
            newErrorId = 'insufficient_funds'
          }
        }
      }

      // Only update errorId if it has actually changed
      if (newErrorId !== errorId) {
        setErrorId(newErrorId)
      }
    }, [
      value,
      outcome?.id,
      currencyId,
      isOrderBookAvailable,
      stableOrderBookSeries,
      marketType,
      user?.id,
      userBalance,
      NotEnoughFunds,
      question?.order_book_enabled,
      currency.display_ratio,
      exchangeRate,
      errorId,
      limitPrice,
      shares,
      orderBook.status,
    ])

    useEffect(() => {
      if (question.is_binary) setPosition(constants.position.LONG)
    }, [question])

    // Prefetch a small set of outcome thumbnails to reduce delay when opening outcome dropdown
    useEffect(() => {
      const outcomes = question?.outcomes || []
      // keep this small to avoid spamming network on huge markets
      const urls = [
        ...new Set(
          outcomes
            .slice(0, 20)
            .flatMap(o => [o?.long_thumbnail, o?.short_thumbnail, o?.thumbnail])
            .filter(Boolean)
        ),
      ]
      urls.forEach(prefetchImage)
    }, [question?.id])

    // Show success check animation when loading completes
    useEffect(() => {
      // Detect when isSaving changes from true to false (success)
      if (prevIsSaving.current && !isSaving) {
        setShowSuccessCheck(true)
        // Hide the check after 800ms
        const timer = setTimeout(() => {
          setShowSuccessCheck(false)
        }, 800)
        return () => clearTimeout(timer)
      }
      prevIsSaving.current = isSaving
    }, [isSaving])

    useEffect(() => {
      // Create a unique key for this currency/question combination
      const currentKey = `${currency.id}-${question?.id || 'no-question'}`

      // Only initialize once per currency/question change, not on orderbook updates
      if (initializedKey.current !== currentKey) {
        setTimeout(() => {
          if (currency.default_bet_amount) {
            const intialValue = getInitialPrice()
            // Consider orderbook max only during initial setup (intentionally not in deps to avoid reset on updates)
            let finalValue = intialValue
            if (isOrderBookAvailable && maxOrderbookAmountFormatted > 0) {
              finalValue = Math.min(intialValue, maxOrderbookAmountFormatted)
            }
            setValue(finalValue)
            initializedKey.current = currentKey
          }
        }, 100)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currency.id, currency.default_bet_amount, question?.id, getInitialPrice])

    useEffect(() => {
      setWarningId()
      if (!currency.is_stable && currency.currency_mode === constants.REAL_MONEY) {
        setWarningId('convert_disclamer')
      }
    }, [currencyId])

    useEffect(() => {
      if (outcomePrice && outcomePrice.current && outcome) {
        const newOutcomePrice = { id: outcome.id, price: outcome.price[parsedCurrencyId] }
        if (outcomePrice.current.id !== outcome.id) {
          outcomePrice.current = newOutcomePrice
        } else if (outcomePrice.current.price !== outcome.price[parsedCurrencyId]) {
          outcomePrice.current = newOutcomePrice
        }
      }
    }, [question, outcome, currencyId])

    useEffect(() => {
      /**
       * ? This is the non order book AMM calculation
       * ? if there is no order book, use the OLD AMM calculation
       */

      if (question?.order_book_enabled || marketType === 'limit' || !position) return

      const unFormatedvalue = normalizeNumericStr(value + '', '.')
      if (Number(unFormatedvalue) < currency.min_bet_amount) return
      const parsedValue = convertToCanonical(Number(unFormatedvalue) * currency.display_ratio, exchangeRate)
      const sharesCanonical = getShares(parsedValue)
      const sharesToDisplay = convertFromCanonical(sharesCanonical, exchangeRate)

      setOutcomeTotalShares(sharesToDisplay)
      setPricePerShare(
        sharesCanonical !== 0
          ? Math.max(
            convertFromCanonical(parsedValue, exchangeRate) /
            formatNumber(sharesToDisplay, locale, 0, currencies[currencyId]?.display_decimal_places, false, true),
            0.01
          )
          : 0
      )
    }, [value, outcome?.id, currencyId, locale, position, marketType, question])

    // Market buy: one simulation for gross shares + fees (same walk as orderBookCalculations.simulateOrderBookBuyByAmount)
    const marketOrderBookFill = useMemo(() => {
      if (marketType !== 'market' || !question?.order_book_enabled || !stableOrderBookSeries?.length) return null
      const numericValue = Number(normalizeNumericStr(value + '', '.'))
      if (!numericValue || numericValue <= 0) return null
      const canonicalValue = convertToCanonical(numericValue * currency.display_ratio, exchangeRate)
      return simulateOrderBookBuyByAmount(stableOrderBookSeries, canonicalValue)
    }, [
      marketType,
      question?.order_book_enabled,
      stableOrderBookSeries,
      value,
      currency.display_ratio,
      exchangeRate,
    ])

    useEffect(() => {
      if (!value || marketType === 'limit') return

      if (question?.order_book_enabled && stableOrderBookSeries && stableOrderBookSeries.length === 0) {
        setOutcomeTotalShares(0)
        setPricePerShare(0)
        setShares(0)
        return
      }

      if (question?.order_book_enabled && stableOrderBookSeries && stableOrderBookSeries.length > 0) {
        if (!marketOrderBookFill) return

        const numericValue = Number(normalizeNumericStr(value + '', '.'))
        const { grossSharesCanonical, lastFilledLevel } = marketOrderBookFill

        const fallbackOrder = stableOrderBookSeries[stableOrderBookSeries.length - 1]
        setClosestValidOrder(lastFilledLevel ?? fallbackOrder)

        if (grossSharesCanonical <= 0) {
          setOutcomeTotalShares(0)
          setPricePerShare(0)
          setShares(0)
          return
        }

        const sharesToWinDisplay = convertFromCanonical(grossSharesCanonical, exchangeRate)
        setOutcomeTotalShares(sharesToWinDisplay)
        setPricePerShare(Number((numericValue / sharesToWinDisplay).toFixed(2)) * currency.display_ratio)
        setShares(parseFloat(grossSharesCanonical.toFixed(2)))
      }
    }, [stableOrderBookSeries, value, question?.order_book_enabled, currency.display_ratio, exchangeRate, marketType, marketOrderBookFill])
    // Set default limitPrice to buySeries last item price and initial shares to getInitialPrice for limit orders
    useEffect(() => {
      if (marketType === 'limit') {
        setLimitPrice(prev => {
          if ((prev === '' || prev === undefined) && stableOrderBookSeries?.length > 0) {
            const lastOrder = stableOrderBookSeries[stableOrderBookSeries.length - 1]
            const formatted = lastOrder.price.toFixed(2)
            return formatted
          }
          return prev
        })
        setShares(prev => (prev === undefined || prev === '' ? String(parseFloat(getInitialPrice().toFixed(2))) : prev))
      }
    }, [marketType, stableOrderBookSeries, currency.id, getInitialPrice])

    // Set default limit price to 0.5 for orderbook error cases
    useEffect(() => {
      if (
        stableOrderBookSeries &&
        isOrderBookAvailable &&
        marketType === 'limit' &&
        stableOrderBookSeries.length === 0
      ) {
        setLimitPrice(prev => {
          if (prev === '' || prev === undefined) {
            return '0.50'
          }
          return prev
        })
      }
    }, [marketType, stableOrderBookSeries, isOrderBookAvailable])

    // Set limitPrice from selectedLimitPrice if provided and in limit mode
    useEffect(() => {
      if (marketType === 'limit' && selectedLimitPrice !== undefined && selectedLimitPrice !== null) {
        setLimitPrice(prev => {
          if (String(selectedLimitPrice) !== String(prev)) {
            return String(selectedLimitPrice)
          }
          return prev
        })
      }
    }, [selectedLimitPrice, marketType])

    // Calculate matched shares for limit buy orders
    useEffect(() => {
      if (marketType !== 'limit' || !limitPrice || !shares || !isOrderBookAvailable || !stableOrderBookSeries) {
        setMatchedShares(null)
        return
      }

      const limitPriceNum = parseFloat(limitPrice)
      const sharesNum = parseFloat(shares)

      if (isNaN(limitPriceNum) || isNaN(sharesNum) || limitPriceNum <= 0 || sharesNum <= 0) {
        setMatchedShares(null)
        return
      }

      // A limit buy matches the asks priced at or below the limit price.
      const matchedSharesCanonical = computeMatchableShares(stableOrderBookSeries, limitPriceNum, { side: 'bid' })

      // If no orders found at or below limit price, no shares will be matched
      if (matchedSharesCanonical === 0) {
        setMatchedShares(null)
        return
      }

      // Convert to display currency
      const matchedSharesDisplay = convertFromCanonical(matchedSharesCanonical, exchangeRate) / currency.display_ratio
      // The matched shares is the minimum of requested shares and available shares
      const finalMatchedShares = Math.min(sharesNum, matchedSharesDisplay)
      setMatchedShares(finalMatchedShares > 0 ? finalMatchedShares : null)
    }, [
      marketType,
      limitPrice,
      shares,
      isOrderBookAvailable,
      stableOrderBookSeries,
      exchangeRate,
      currency.display_ratio,
    ])

    // Separate effect to set shares only when selectedLimitPrice changes (not when orderbook changes)
    useEffect(() => {
      if (marketType === 'limit' && selectedLimitPrice !== undefined && selectedLimitPrice !== null) {
        // Set shares to cumulative_shares for the selected price
        if (stableOrderBookSeries || orderBook?.sellSeries) {
          const match = [...(stableOrderBookSeries || []), ...(orderBook?.sellSeries || [])].find(
            o => String(o.price) === String(selectedLimitPrice)
          )
          if (match) {
            setShares(parseFloat(match.cumulative_shares.toFixed(2)))
          }
        }
      }
    }, [selectedLimitPrice, marketType]) // Removed stableOrderBookSeries and orderBook?.sellSeries from dependencies

    useEffect(() => {
      if (!adminMode || !outcome?.id || !question?.order_book_enabled || !stableOrderBookSeries?.length) {
        dispatch(clearLimitOrderFillPreview())
        return
      }

      let previewByPrice = {}

      if (marketType === 'limit') {
        const sharesNum = parseFloat(shares) || 0
        const limitPriceNum = parseFloat(limitPrice) || 0
        if (sharesNum <= 0 || limitPriceNum <= 0) {
          dispatch(clearLimitOrderFillPreview())
          return
        }
        const sharesCanonical = convertToCanonical(sharesNum * currency.display_ratio, exchangeRate)
        previewByPrice = computeLimitBuyFillPreview(stableOrderBookSeries, sharesCanonical, {
          maxLimitPrice: limitPriceNum,
        })
      } else if (marketType === 'market') {
        if (!marketOrderBookFill?.grossSharesCanonical || marketOrderBookFill.grossSharesCanonical <= 0) {
          dispatch(clearLimitOrderFillPreview())
          return
        }
        previewByPrice = computeLimitBuyFillPreview(
          stableOrderBookSeries,
          marketOrderBookFill.grossSharesCanonical
        )
      } else {
        dispatch(clearLimitOrderFillPreview())
        return
      }

      dispatch(
        setLimitOrderFillPreview({
          outcomeId: outcome.id,
          position,
          previewByPrice,
        })
      )

      return () => dispatch(clearLimitOrderFillPreview())
    }, [
      adminMode,
      marketType,
      shares,
      limitPrice,
      value,
      marketOrderBookFill,
      outcome?.id,
      position,
      question?.order_book_enabled,
      stableOrderBookSeries,
      currency.display_ratio,
      exchangeRate,
      dispatch,
    ])

    // Fee calculation for display (taker fee on matched portion). Market uses feeMessageType like SellPlaceBet.
    const feeResult = useMemo(() => {
      const empty = { feeDisplay: null, feeMessageType: null }
      if (!stableOrderBookSeries?.length || !outcome) return empty
      if (marketType === 'market') {
        const numericValue = Number(normalizeNumericStr(value + '', '.'))
        if (!numericValue || numericValue <= 0) return empty
        if (!marketOrderBookFill) return empty
        const feeRaw = convertFromCanonical(marketOrderBookFill.totalFeeCanonical, exchangeRate)
        const feeDisplay = feeRaw / currency.display_ratio
        return { feeDisplay, feeMessageType: 'includes' }
      }
      // Limit
      if (matchedShares == null || matchedShares <= 0) return empty
      const matchedSharesCanonical = convertToCanonical(matchedShares * currency.display_ratio, exchangeRate)
      const { totalFee } = calculateFeesForShares(stableOrderBookSeries, matchedSharesCanonical, {
        fillPriceDirection: 'asc',
      })
      const feeRaw = convertFromCanonical(totalFee, exchangeRate)
      const feeDisplay = feeRaw / currency.display_ratio
      return { feeDisplay, feeMessageType: null }
    }, [
      marketType,
      value,
      shares,
      matchedShares,
      stableOrderBookSeries,
      outcome,
      currency.display_ratio,
      exchangeRate,
      marketOrderBookFill,
    ])

    const limitFeeBreakdown = useMemo(() => {
      if (marketType !== 'limit') return null

      const limitPriceNum = parseFloat(limitPrice) || 0
      const sharesNum = parseFloat(shares) || 0
      const matchedSharesNum = Math.max(0, parseFloat(matchedShares) || 0)
      const unmatchedShares = Math.max(0, sharesNum - matchedSharesNum)

      let maxToPay = limitPriceNum * sharesNum

      if (stableOrderBookSeries?.length && limitPriceNum > 0 && sharesNum > 0) {
        const getBestFillPrice = () => {
          const exactMatch = stableOrderBookSeries.find(order => Math.abs(order.price - limitPriceNum) < 0.001)
          const levelOrder = exactMatch ?? stableOrderBookSeries.find(order => order.price <= limitPriceNum)
          return levelOrder?.price ?? limitPriceNum
        }

        let fillLevelPrice = getBestFillPrice()
        let matchedCost = 0

        if (matchedSharesNum > 0) {
          const matchedCanonical = convertToCanonical(matchedSharesNum * currency.display_ratio, exchangeRate)
          const matchedSim = simulateOrderBookBuyByShares(stableOrderBookSeries, matchedCanonical, {
            maxLimitPrice: limitPriceNum,
          })
          const fillLevel = matchedSim.lastFilledLevel

          if (fillLevel?.price != null) {
            fillLevelPrice = fillLevel.price
          }

          const useLevelCumulativeAmount =
            fillLevel?.cumulative_amount != null &&
            fillLevel?.cumulative_shares != null &&
            Math.abs(matchedSim.sharesConsumed - fillLevel.cumulative_shares) < 1e-9

          const matchedAmountCanonical = useLevelCumulativeAmount
            ? fillLevel.cumulative_amount
            : matchedSim.grossAmountCanonical + matchedSim.totalFeeCanonical 
          matchedCost =
            convertFromCanonical(matchedAmountCanonical, exchangeRate) / currency.display_ratio
        }
        maxToPay = matchedCost + unmatchedShares * fillLevelPrice
      }

      const takerFee = matchedShares != null && matchedShares > 0 && feeResult?.feeDisplay != null ? feeResult.feeDisplay : 0
      const rebate = computeMakerRebate({ shares, matchedShares, price: limitPrice })

      return {
        maxToPay,
        maxToPayWithFees: maxToPay,
        takerFee,
        rebate,
      }
    }, [
      marketType,
      limitPrice,
      shares,
      matchedShares,
      feeResult?.feeDisplay,
      stableOrderBookSeries,
      exchangeRate,
      currency.display_ratio,
    ])

    const convertToUsd = (_value, currencyId) => {
      if (exchangeRateStatus === constants.LOADING) return 0
      if (isNaN(Number(_value))) return formatNumber(0, locale, 2, 8)

      // Get the exchange rate for the currency
      const rate = exchangeRateAll?.USD?.rates[currencyId]
      if (!rate) return 0

      // Convert to USD
      const usdValue = Number(currencyId === 'BTC' ? _value / 1000 : _value) / rate
      return usdValue
    }

    function getShares(parsedValue) {
      if (parsedValue === 0 || !outcome) return 0

      const tax = isRealMoney ? question.tax_real_money : question.tax_play_money
      return wagerService.getShares(
        question.scoring_rule,
        question.outcomes
          .filter(o => !o.disabled)
          .map(o => o.shares[isRealMoney ? question.canonical_currency : 'OOM']),
        question.outcomes.filter(o => !o.disabled).indexOf(question.outcomes.find(o => o && o.id === outcome.id)),
        parsedValue,
        tax,
        position
      )
    }

    // Helper function to handle order conflict errors
    const handleOrderConflictError = async (error, outcomeId, amount, currencyId) => {
      if (user?.settings?.auto_cancel_conflicting_orders) {
        // Automatically retry with cancel_conflicting_orders=true
        await onMakeBet(outcomeId, amount, currencyId, true)
        return true // Indicates error was handled
      }

      // Call the order conflict callback
      onOrderConflict({
        onRetry: (cancelConflictingOrders, dontAskAgain) => {
          onMakeBet(outcomeId, amount, currencyId, cancelConflictingOrders, dontAskAgain)
        },
      })
      return true
    }

    // Helper function to handle invalid amount errors
    const handleInvalidAmountError = (outcomeId, currencyId) => {
      // Re-fetch the order book to get updated data
      dispatch(
        fetchOrderBook({
          question: question.id,
          currency_mode: currencyId === 'OOM' ? 'play_money' : 'real_money',
          outcome: outcomeId,
          position: position,
          source: 'buy_place_bet',
          showErrorToast: true,
        })
      )
    }

    // Helper function to handle authentication errors
    const handleAuthenticationError = () => {
      openLogin()
      dispatch(resetCurrentUser())
    }

    const onMakeBet = async (outcomeId, amount, currencyId, cancelConflictingOrders = false, dontAskAgain = false) => {
      try {
        const onSuccess = () => {
          // Update user settings if they chose "don't ask again"
          if (cancelConflictingOrders && dontAskAgain) {
            dispatch(updateCurrentUserSettings({ auto_cancel_conflicting_orders: true }))
          }

          if (marketType === 'limit') {
            dispatch(setSelectedLimitPrice(closestValidOrder?.price))
            setShares(parseFloat(closestValidOrder?.cumulative_shares.toFixed(2)))
            setCalendarDate({})

            dispatch(
              fetchUserOrders({
                status: 'open',
                limit: 50,
                clear: true,
                question: question?.id,
              })
            )
          } else {
            setValue(getInitialPrice())
          }
          toastService('wagerBuy', {
            question,
            title: marketType === 'market' ? i18n('place_bet.bought') : i18n('place_bet.limit_order_placed'),
          })
        }

        // Get the expiration date if custom or end_of_day was selected
        let expirationDate = null
        if (expirationOption === 'custom' && calendarDate.confirmedDate) {
          expirationDate = new Date(calendarDate.confirmedDate)
        } else if (expirationOption === 'end_of_day') {
          // Set to end of today (23:59:59)
          const now = new Date()
          expirationDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)
        }

        const price = marketType === 'market' ? null : Number(limitPrice)
        const _shares = marketType === 'market'
          ? Number(parseFloat((outcomeTotalShares)))
          : Number(shares)
        const side = 'bid'
        if (isOrderBookAvailable || marketType === 'limit') {
          await dispatch(
            placeLimitOrder(
              outcomeId,
              _shares,
              price,
              currencyId,
              position,
              expirationOption,
              expirationDate,
              place,
              side,
              onSuccess,
              marketType === 'market' ? Number(value) : undefined,
              cancelConflictingOrders
            )
          )
        } else {
          // ? this is when the user is placing a market order with no order book data
          const amountInBtc = convertToCanonical(amount, exchangeRate)
          const amountInUsdc = wagerService.ceilPrice(convertFromCanonical(amountInBtc, exchangeRateUsdc), 2)

          const rate =
            currencyId === 'BTC'
              ? currencies[currencyId]?.display_decimal_places * 2
              : currencies[currencyId]?.display_decimal_places
          const parsedAmount = wagerService.floorPrice(Number(amount), rate)

          await dispatch(
            placeBet(
              outcomeId,
              _shares,
              parsedAmount,
              amountInUsdc,
              currencyId,
              exchangeUpdateCode,
              quickbet,
              place,
              position
            )
          )
        }
        if (isMobileSize) onChangeOutcome(null)
        if (onWagerAction) onWagerAction()
        if (quickbet || isMobileSize) onToggle()
      } catch (err) {
        // Use centralized error handling from wagers.js
        const errorInfo = getErrorInfo(err)

        // Handle order conflict errors with async support
        if (errorInfo?.code === 'OrderBookConflictingOrders') {
          const handled = await handleOrderConflictError(err, outcomeId, amount, currencyId)
          if (handled) {
            return // Error was handled (auto-retry or user will handle via UI)
          }
        }

        // Handle invalid amount error - toast is already shown in thunk, just handle side effects
        if (errorInfo?.code === 'OrderBookInvalidAmount') {
          handleInvalidAmountError(outcomeId, currencyId)
          return // Don't show toast again, thunk already handled it
        }

        // Use centralized handler for other errors (invalid amount is handled above)
        const result = handleOrderError(err, {
          showToast: true,
          onAuthenticationError: () => {
            handleAuthenticationError()
          },
          onGenericError: errorInfo => {
            if (errorInfo?.code) {
              toastService('fail', buildErrorToast(err, 'api_errors.titles.place_bet'))
            }
          },
        })

        // If error was handled and shouldn't be re-thrown, return early
        if (result.handled && result.shouldRethrow === false) {
          return
        }
      }
    }

    function checkValidInput() {
      let newError = ''
      const unFormatedvalue = normalizeNumericStr(value + '', '.')

      if (!outcome) newError = 'outcome_not_selected'
      const _normalizeNumericStr = unFormatedvalue
      const _userBalace = currencyId === 'BTC' ? userBalance * 1000 : userBalance

      if (!user) {
        // openLogin()
      } else if (!fromAllowedCountry && currencyId !== 'OOM') {
        newError = 'from_forbidden_country'
      } else if (marketType === 'market') {
        // Get the maximum cumulative_amount from orderbook if available
        const maxOrderbookAmount =
          isOrderBookAvailable && orderBook?.buySeries?.length > 0
            ? Math.max(...orderBook.buySeries.map(order => order.cumulative_amount))
            : null

        const canonicalValue = convertToCanonical(Number(_normalizeNumericStr) * currency.display_ratio, exchangeRate)
        // if (Number(_normalizeNumericStr) > maxTransactionPriceFormatted) {
        //   newError = 'amount_beyond_max'
        // } else
        if (Number(_normalizeNumericStr) < currency.min_bet_amount) {
          newError = 'amount_below_min'
        } else if (
          isOrderBookAvailable &&
          orderBook?.buySeries &&
          orderBook.buySeries.length === 0 &&
          orderBook.status === constants.COMPLETE
        ) {
          newError = 'no_orderbook_orders'
        } else if (maxOrderbookAmount && canonicalValue > maxOrderbookAmount) {
          newError = 'amount_beyond_orderbook_max'
        } else if (Number(_normalizeNumericStr) > _userBalace || NotEnoughFunds) {
          newError = 'insufficient_funds'
        } else if (pricePerShareRounded > 1) {
          newError = 'max_one_price_per_share'
        }
      } else {
        // Limit order validation
        if (parseFloat(limitPrice) > 0.99) {
          newError = 'limit_price_too_high'
        }

        // Validate shares input
        if (!shares) {
          newError = 'shares_required'
        } else if (isNaN(parseFloat(shares)) || parseFloat(shares) <= 0) {
          newError = 'invalid_shares'
        }

        // Validate minimum amount and funds for limit orders
        const maxCost = parseFloat(limitPrice) * parseFloat(shares)
        if (maxCost < currency.min_bet_amount) {
          newError = 'amount_below_min'
        } else if (maxCost > _userBalace && currencyId !== 'OOM') {
          newError = 'insufficient_funds'
        }
      }
      setErrorId(newError)
      return Boolean(newError)
    }

    const onFormSubmit = async () => {
      // Check email confirmation for all users (both wallet and non-wallet users)
      if (user && !user.email_confirmed) {
        onToggle()
        showModal({
          title: ' ',
          showCloseButton: true,
          content: <EmailNotConfirmed />,
        })
        return
      }

      const unFormatedvalue = normalizeNumericStr(value + '', '.')
      if (!checkValidInput() && !errorId) {
        if (!user) return

        // Place the bet first (for both wallet and non-wallet users)
        if (marketType === 'limit') {
          // Handle limit order submission
          await onMakeBet(outcome.id, parseFloat(limitPrice) * parseFloat(shares), currencyId)
        } else {
          // Handle market order submission
          const amount = currencyId === 'BTC' ? unFormatedvalue / 1000 : unFormatedvalue
          textField.current && textField.current.blur()
          await onMakeBet(outcome.id, amount, currencyId)
        }
        setBetPlaced(true)
        setValue(getInitialPrice())
      }
    }

    // Shares the balance can cover at the current limit price.
    const affordableShares = useMemo(() => {
      const price = parseFloat(limitPrice)
      if (!price || price <= 0) return 0
      const _userBalance = userBalance == null ? null : currencyId === 'BTC' ? userBalance * 1000 : userBalance
      if (_userBalance == null) return 0
      return Math.floor((_userBalance / price) * 100) / 100
    }, [limitPrice, userBalance, currencyId])

    const renderMatchedSharesChip = () => {
      if (errorId || matchedShares === null || matchedShares <= 0) return null

      return (
        <OverlayPanel
          content={
            <p className="text-sm text-Neutral-6 w-40">
              {formatNumber(matchedShares, locale, 2, currency?.display_decimal_places || 2)}{' '}
              {i18n('place_bet.shares_from_order_executed')}
            </p>
          }
          customPosition={{ top: '24px', right: '0px', left: 'auto' }}
          showMode={isMobileSize ? 'click' : 'hover'}
          hideOnClickOutside
          className="!px-4 !py-3 !rounded-[var(--radius-sm)] !min-w-fit max-w-10 shadow-xl border border-surface-3">
          <span className="cursor-pointer text-positive-4">
            {formatNumber(matchedShares, locale, 0, currency?.display_decimal_places || 2)}{' '}
            {i18n('place_bet.matching')}
          </span>
        </OverlayPanel>
      )
    }

    const renderLimitInputs = () => {
      const limitPriceError = errorId === 'limit_price_too_high'
      const sharesError = errorId === 'shares_required' || errorId === 'invalid_shares'

      return (
        <div>
          <div
            className={classNames(
              'rounded-[var(--radius-sm)] border border-solid border-Neutral-3 divide-y divide-Neutral-3 transition duration-200 focus-within:border-[var(--color-brand-04)]',
              { '!border-negative-4': limitPriceError || sharesError }
            )}>
            <PlaceBetInput
              testId="place-bet-buy-limit-price-input"
              variant="row"
              value={limitPrice}
              onChange={value => {
                // Allow empty string or values that could potentially become <= 0.99
                if (
                  value === '' ||
                  value === '0' ||
                  value === '0.' ||
                  (parseFloat(value) <= 0.99 && !isNaN(parseFloat(value)))
                ) {
                  setLimitPrice(value)
                }
              }}
              decimalScale={2}
              mode="limit"
              label={i18n('place_bet.limit_price')}
              step={0.01}
              locale={locale}
              currencyId={currencyId}
              noCurrency={true}
            />

            <PlaceBetInput
              testId="place-bet-buy-shares-input"
              variant="stacked"
              value={shares}
              onChange={setShares}
              decimalScale={2}
              mode="limit"
              label={i18n('place_bet.shares')}
              step={1}
              locale={locale}
              currencyId={currencyId}
              chipSet="sharesIncrement"
              max={affordableShares > 0 ? wagerService.floorPrice(affordableShares, 2) : undefined}
              noCurrency={true}
              helperText={renderMatchedSharesChip()}
              onChipSelect={chipValue => {
                if (chipValue !== 'max') return
                setShares(String(wagerService.floorPrice(affordableShares, 2)))
              }}
            />
          </div>

          {(limitPriceError || sharesError) && (
            <div className="mt-1 text-negative-4 text-xs" data-testid={`place-bet-error-${errorId}`}>
              {errorTypes[errorId]}
            </div>
          )}
          {warningId && <div className="mt-1 text-negative-4 text-xs">{warningTypes[warningId]}</div>}
        </div>
      )
    }

    const renderCalendarContent = () => {
      return (
        <div className={isMobileSize ? 'grid justify-items-stretch' : 'grid justify-items-end'}>
          <Calendar
            fullWidth={isMobileSize}
            mode="single"
            value={calendarDate.selectedDate || new Date()}
            onChange={date => {
              // returns date Fri Jun 20 2025 00:00:00 GMT+0100 (West Africa Standard Time)
              setCalendarDate(dates => ({ ...dates, selectedDate: date }))
            }}
            minDate={new Date()}
          // Optionally set maxDate if needed
          />
          <Button
            onClick={() => {
              setCalendarDate(dates => ({ ...dates, confirmedDate: dates.selectedDate }))
              setCalendarOpen(false)
            }}
            className="my-4"
            variant="primary"
            size="large">
            {i18n('place_bet.confirm')}
          </Button>
        </div>
      )
    }

    const renderExpirationRow = () => {
      const customLabel =
        expirationOption === 'custom' && calendarDate.confirmedDate
          ? new Date(calendarDate.confirmedDate).toLocaleDateString()
          : i18n('place_bet.custom')

      const expirationOptions = [
        { value: 'until_cancelled', text: i18n('place_bet.no_expiration') },
        { value: 'end_of_day', text: i18n('place_bet.end_of_day') },
        { value: 'custom', text: customLabel },
      ]

      return (
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-Neutral-6">{i18n('place_bet.expiration_date')}</span>
          <Dropdown
            dropdownType="list"
            value={expirationOption}
            list={expirationOptions}
            arrow="outlined"
            singleOptionStyle="tick"
            className="expiration-dropdown"
            ariaLabel={i18n('place_bet.expiration_date')}
            onChange={optionValue => {
              setExpirationOption(optionValue)
              if (optionValue === 'custom') setCalendarOpen(true)
            }}
          />
        </div>
      )
    }

    const renderMarketInput = () => (
      <>
        <PlaceBetInput
          testId="place-bet-buy-amount-input"
          value={value}
          onChange={setValue}
          decimalScale={currencies[currencyId]?.display_decimal_places}
          pricePerShare={pricePerShare}
          locale={locale}
          hasError={Boolean(errorId)}
          chipSet="buy"
          currencyId={currencyId}
          onChipSelect={max => {
            const _userBalance =
              userBalance == null ? null : currencyId === 'BTC' ? userBalance * 1000 : userBalance

            let maxAmount
            if (_userBalance == null) {
              // Balance still loading — Math.min(null, x) is 0; fall back to orderbook or default stake
              maxAmount =
                isOrderBookAvailable && maxOrderbookAmountFormatted > 0
                  ? maxOrderbookAmountFormatted
                  : getInitialPrice()
            } else {
              maxAmount = _userBalance
              if (isOrderBookAvailable && maxOrderbookAmountFormatted !== null) {
                maxAmount = Math.min(_userBalance, maxOrderbookAmountFormatted)
              }
            }

            const formattedMaxAmount = wagerService.floorPrice(maxAmount, maxDecimalPlaces)
            setValue(formattedMaxAmount)
          }}
        />
        {errorId && errorId !== 'outcome_not_selected' && (
          <div className="mt-1 text-negative-4 text-xs" data-testid={`place-bet-error-${errorId}`}>{errorTypes[errorId]}</div>
        )}
        {warningId && <div className="mt-1 text-negative-4 text-xs">{warningTypes[warningId]}</div>}

        {!errorId && (
          <div className="flex justify-between items-start gap-2 mt-1">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              {currency.currency_type === 'crypto' && !currency.is_stable && (
                <div className="text-xs text-Neutral-4">
                  {'≈  $ '} {formatNumber(Number(convertToUsd(value, currencyId)), locale, 0, 2)}
                </div>
              )}
              {pricePerShare > 0 && (
                <div className="text-xs text-Neutral-4">
                  {i18n('place_bet.average_price')} {pricePerShareRounded === 0.01 && '< '}
                  {pricePerShareRounded.toFixed(2)}
                </div>
              )}
            </div>

          </div>
        )}
      </>
    )

    const renderCostInfo = () => (
      <div className="flex items-center justify-between gap-1">
        <span className="text-sm text-Neutral-4">{i18n('place_bet.cost')}</span>
        {outcome ? (
          <Currency
            value={limitFeeBreakdown?.maxToPay ?? (parseFloat(limitPrice) || 0) * (parseFloat(shares) || 0)}
            currency={currencyId}
            showPrefix
            decimalPlaces={currencies[currencyId]?.display_decimal_places}
            valueClassName="text-sm"
            className="text-sm font-semibold"
            roundingMode="halfCeil"
            noNumberFlow
          />
        ) : (
          <span className="text-sm">-</span>
        )}
      </div>
    )

    const renderBetInfo = () => {
      const showMarketFeeTooltip =
        marketType === 'market' &&
        feeResult?.feeMessageType === 'includes' &&
        feeResult.feeDisplay != null
      const showLimitFeeBreakdownTooltip = marketType === 'limit' && Boolean(limitFeeBreakdown)

      return (
      <div className="flex items-center justify-between  gap-1">
        <span className="text-sm text-Neutral-4 flex items-center gap-1">
          <span className="whitespace-nowrap">{i18n('place_bet.to_win')}</span>

          {(showMarketFeeTooltip || showLimitFeeBreakdownTooltip) && (
            <OverlayPanel
              content={
                showMarketFeeTooltip ? (
                  <p className="text-sm text-Neutral-6">
                    {i18n('place_bet.fee_includes', {
                      amount:
                        formatNumber(feeResult.feeDisplay, locale, 2, currency?.display_decimal_places ?? 2) +
                        ' ' +
                        (currency?.display_code ?? ''),
                    })}
                  </p>
                ) : (
                  <div className="min-w-[200px] space-y-3 text-sm text-Neutral-6">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-Neutral-4">{i18n('place_bet.fee')}</span>
                      <Currency
                        value={limitFeeBreakdown.takerFee || 0}
                        currency={currencyId}
                        showPrefix
                        decimalPlaces={currencies[currencyId]?.display_decimal_places}
                        valueClassName="text-sm"
                        className="text-sm"
                        roundingMode="halfCeil"
                        noNumberFlow
                      />
                    </div>

                    {limitFeeBreakdown.rebate > 0 && (
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-Neutral-4">{i18n('place_bet.rebate')}</span>
                        <Currency
                          value={limitFeeBreakdown.rebate}
                          currency={currencyId}
                          showPrefix
                          decimalPlaces={currencies[currencyId]?.display_decimal_places}
                          valueClassName="text-sm text-brand-gradient"
                          className="text-sm font-semibold"
                          roundingMode="halfCeil"
                          noNumberFlow
                        />
                      </div>
                    )}
                    <a
                      href={marketMakerDocumentationUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full py-1 text-xs text-center font-normal hover:underline"
                    >
                      {i18n('convert_to_stable_coin_modal.know_more')}
                    </a>
                  </div>
                )
              }
              customPosition={
                showMarketFeeTooltip
                  ? { top: '-50px', right: 'auto', left: '0px' }
                  : { top: '-110px', right: 'auto', left: '10px' }
              }
              showMode={isMobileSize ? 'click' : 'hover'}
              hideOnClickOutside
              className="!px-4 !py-3 !rounded-[var(--radius-sm)] shadow-3xl max-w-xs break-words"
            >
              <InfoIcon className="cursor-pointer" width="14px" height="14px" />
            </OverlayPanel>
          )}
        </span>
        {outcome ? (
          <>
            <Currency
              value={marketType === 'limit'
                ? shares
                : Math.abs(outcomeTotalShares)}
              currency={currencyId}
              showPrefix
              valueClassName="text-positive-4 text-2xl"
              className="font-semibold leading-snug "
              decimalPlaces={currencies[currencyId]?.display_decimal_places}
            />
            {currency.currency_type === 'crypto' && !currency.is_stable && (
              <div className="text-xs text-Neutral-4">
                {'≈  $ '}{' '}
                {formatNumber(
                  Number(
                    convertToUsd(
                      marketType === 'limit'
                        ? Math.abs(parseFloat(shares))
                        : Math.abs(currencyId === 'BTC' ? outcomeTotalShares * 1000 : outcomeTotalShares),
                      currencyId
                    )
                  ),
                  locale,
                  0,
                  2
                )}
              </div>
            )}
          </>
        ) : (
          '-'
        )}

      </div>
      )
    }

    const loginToBetButtonOnClick = () => {
      onToggle()
      openLogin()
    }

    const renderSubmitButton = () => {
      const ui = deriveMarketUi(question)
      // Independent mono custom positions: UI uses virtual outcomes, but API uses physical outcome + position
      const isVirtualOutcome = ui.kind === 'independent_mono_custom'
      const labelOutcome = outcome || question?.outcomes?.[0]
      const isYesNoOrUpDown =
        ui.binaryLabelStyle === 'yesno' ||
        ui.binaryLabelStyle === 'updown' ||
        labelOutcome?.position_labels === 'yesno' ||
        labelOutcome?.position_labels === 'updown'

      // Button text and color (defined early so we can show same bet button when user is still loading)
      const getButtonText = () => {
        if (showSuccessCheck) {
          return (
            <>
              <style>{`
                @keyframes checkmarkScaleIn {
                  from {
                    transform: scale(0);
                    opacity: 0;
                  }
                  to {
                    transform: scale(1);
                    opacity: 1;
                  }
                }
              `}</style>
              <div className="flex items-center justify-center">
                <CheckIcon
                  className="w-6 h-6"
                  style={{
                    animation: 'checkmarkScaleIn 0.4s ease-out',
                  }}
                  fillcolor="currentColor"
                />
              </div>
            </>
          )
        }

        const actionLabel = i18n('place_bet.buy')

        if (isVirtualOutcome) {
          return actionLabel
        }

        // Only add "Buy {label}" suffix for yes/no or up/down label sets.
        if (!isYesNoOrUpDown) return actionLabel

        const positionLabels = getPositionLabels(labelOutcome, question)

        // Yes/no or up/down: the selected POSITION determines the label.
        const suffix = position === constants.position.LONG ? positionLabels.longLabel : positionLabels.shortLabel
        return (
          <>
            {actionLabel} {suffix}
          </>
        )
      }

      const getButtonColorClasses = () => {
        if (ui.kind === 'independent_mono_custom') {
          return '!bg-brand-4 hover:bg-brand-4/90! !text-[var(--color-contrast-01)]'
        }

        // Custom position labels: always use brand color for the active buy button (regardless of LONG/SHORT).
        if (!isYesNoOrUpDown) {
          return '!bg-brand-4 hover:bg-brand-4/90! !text-[var(--color-contrast-01)]'
        }

        if (position === constants.position.SHORT) {
          return '!bg-auxiliaries-3 hover:bg-auxiliaries-3/90! !text-white'
        }
        return '!bg-positive-3 hover:bg-positive-3/90! !text-white'
      }

      // User not loaded yet: show bet button (predict logged in) but disabled
      const userStillLoading = !user && (currentUserStatus === constants.LOADING || currentUserStatus === constants.IDLE)
      if (!user) {
        if (userStillLoading) {
          return (
            <Button
              variant="primary"
              type="solid"
              size="large"
              disabled
              className={classNames('h-12 font-bold rounded-[var(--radius-sm)] transition-colors opacity-50 cursor-not-allowed', getButtonColorClasses())}>
              {getButtonText()}
            </Button>
          )
        }
        return (
          <Button
            variant="primary"
            type="solid"
            size="large"
            className="h-12 font-bold text-white rounded-[var(--radius-sm)] !bg-brand-4 hover:bg-brand-4/90!"
            onClick={loginToBetButtonOnClick}>
            {i18n('place_bet.login_to_bet')}
          </Button>
        )
      }

      // Calculate insufficient funds
      const unFormatedvalue = normalizeNumericStr(value + '', '.')
      const _userBalance = currencyId === 'BTC' ? userBalance * 1000 : userBalance
      const insufficientFunds =
        marketType === 'market'
          ? Number(unFormatedvalue) > _userBalance || NotEnoughFunds
          : marketType === 'limit'
            ? parseFloat(limitPrice) * parseFloat(shares) > _userBalance
            : false

      // Handle orderbook errors - show change to limit order button
      if (errorId === 'amount_beyond_orderbook_max' || errorId === 'no_orderbook_orders') {
        let suggestedPrice = 0.5 // Default fallback price

        if (errorId === 'amount_beyond_orderbook_max' && orderBook?.buySeries?.length > 0) {
          const maxOrder = orderBook.buySeries[orderBook.buySeries.length - 1]
          suggestedPrice = maxOrder?.price || 0.5
        }

        return (
          <Button
            variant="primary"
            type="solid"
            size="large"
            className="h-12 font-bold text-white rounded-[var(--radius-sm)] !bg-brand-4 hover:bg-brand-4/90!"
            onClick={() => onChangeToLimitOrder(suggestedPrice)}>
            {i18n('place_bet.change_to_limit_order')}
          </Button>
        )
      }

      // Handle currency conversion warning
      if (warningId === 'convert_disclamer') {
        return (
          <Button
            onClick={() => {
              onToggle()
              navigate(convertUrl(currency.id))
            }}
            variant="primary"
            type="solid"
            size="large"
            className="h-12 font-bold rounded-[var(--radius-sm)]">
            {i18n('place_bet.convert')}
          </Button>
        )
      }

      // Handle insufficient funds
      if (insufficientFunds && currencyId !== 'OOM') {
        return (
          <Button
            variant="primary"
            type="solid"
            size="large"
            className="h-12 font-bold rounded-[var(--radius-sm)] !bg-brand-4 hover:bg-brand-4/90!"
            onClick={() => {
              mixpanelService.trackClick({
                component: CLICK_COMPONENT.DEPOSIT_ENTRY,
                element: CLICK_ELEMENT.CTA,
                source: CLICK_SOURCE.BET_SLIP,
                destination: depositUrl(currency.id, 'bet_slip'),
                market_id: question?.id,
                market_title: question?.title,
              })
              navigate(depositUrl(currency.id, 'bet_slip'))
            }}>
            {i18n('place_bet.deposit_to_bet')}
          </Button>
        )
      }

      // Main submit button
      const buttonColorClasses = getButtonColorClasses()
      return (
        <Button
          data-testid="place-bet-submit-button"
          data-action="buy"
          loading={isSaving && !showSuccessCheck}
          disabled={
            !isCalculationReady ||
            Boolean(isSaving) ||
            (insufficientFunds && currencyId === 'OOM')
          }
          variant="primary"
          type="solid"
          size="large"
          className={classNames('h-12 font-bold rounded-[var(--radius-sm)] transition-colors', buttonColorClasses, {
            'opacity-50 !cursor-not-allowed':
              !isCalculationReady || isSaving || (insufficientFunds && currencyId === 'OOM'),
          })}
          onClick={onFormSubmit}>
          {getButtonText()}
        </Button>
      )
    }

    const renderOutcomeSelector = () => {
      const ui = deriveMarketUi(question)
      const singleOutcome = question.outcomes?.[0]

      // Position labels should reflect the currently selected outcome (when available).
      // If no outcome is selected yet, fall back to the first available outcome.
      const labelOutcome = outcome || singleOutcome || question.outcomes?.[0]
      const labels = getPositionLabels(labelOutcome, question)
      const firstLabel = labels.longLabel
      const secondLabel = labels.shortLabel
      const isYesNoOrUpDown =
        ui.binaryLabelStyle === 'yesno' ||
        ui.binaryLabelStyle === 'updown' ||
        labelOutcome?.position_labels === 'yesno' ||
        labelOutcome?.position_labels === 'updown'

      /* Detail page carries the odds on the buttons themselves; the quick-bet popover in the
         feed sits under a card that already shows them. */
      const priceKey = currencyId === currencies.OOM.id ? currencies.OOM.id : question.canonical_currency
      const showPositionOdds = !quickbet && isYesNoOrUpDown

      const shouldShowUpDownArrows =
        !showPositionOdds &&
        (outcome?.position_labels === 'updown' || singleOutcome?.position_labels === 'updown')

      let firstOdds = null
      let secondOdds = null
      if (showPositionOdds) {
        if (ui.isMono) {
          const longPrice = singleOutcome?.price?.[priceKey]
          if (Number.isFinite(longPrice)) {
            firstOdds = `${Math.round(100 * longPrice)}%`
            secondOdds = `${Math.round(100 * (1 - longPrice))}%`
          }
        } else {
          /* The buttons set LONG/SHORT on the selected outcome, so both percentages have to
             come from that outcome rather than from positional slots in `question.outcomes`. */
          const longPrice = labelOutcome?.price?.[priceKey]
          if (Number.isFinite(longPrice)) {
            firstOdds = `${Math.round(100 * longPrice)}%`
            secondOdds = `${Math.round(100 * (1 - longPrice))}%`
          }
        }
      }

      const isFirstButtonHighlighted = position === constants.position.LONG
      const isSecondButtonHighlighted = position === constants.position.SHORT

      const positionButtons = (
        <div className="flex w-full gap-2">
          <button
            onClick={() => {
              if (ui.isMono) {
                if (!outcome || outcome.id !== singleOutcome?.id) {
                  onChangeOutcome(singleOutcome)
                }
                setPosition(constants.position.LONG)
                return
              }
              setPosition(constants.position.LONG)
            }}
            className={classNames(
              'flex justify-center cursor-pointer items-center h-12 px-4 w-full rounded-[var(--radius-sm)] transition-all duration-200',
              isYesNoOrUpDown
                ? 'bg-positive-5/30 text-Neutral-6 hover:bg-positive-5/50'
                : 'bg-Neutral-2 text-Neutral-6 hover:bg-Neutral-3',
              {
                'font-bold !text-white': isFirstButtonHighlighted,
                '!bg-Neutral-3 !text-Neutral-4': isSecondButtonHighlighted,
                '!bg-positive-3': isYesNoOrUpDown && isFirstButtonHighlighted,
                '!bg-[var(--color-brand-04)]/90': !isYesNoOrUpDown && isFirstButtonHighlighted,
              }
            )}>
            <span
              className={classNames(
                'flex items-center gap-2',
                firstOdds ? 'w-full justify-between text-base leading-none' : 'text-lg font-medium'
              )}>
              {shouldShowUpDownArrows && <ArrowUp width="16" height="16" />}
              {firstOdds ? <span className="font-normal">{firstLabel}</span> : firstLabel}
              {firstOdds && <span className="font-bold tabular-nums">{firstOdds}</span>}
            </span>
          </button>
          <button
            onClick={() => {
              if (ui.isMono) {
                if (!outcome || outcome.id !== singleOutcome?.id) {
                  onChangeOutcome(singleOutcome)
                }
                setPosition(constants.position.SHORT)
                return
              }
              setPosition(constants.position.SHORT)
            }}
            className={classNames(
              'flex justify-center cursor-pointer items-center h-12 px-4 w-full rounded-[var(--radius-sm)] transition-all duration-200',
              isYesNoOrUpDown
                ? 'bg-auxiliaries-3/30 text-Neutral-6 hover:bg-auxiliaries-3/50'
                : 'bg-Neutral-2 text-Neutral-6 hover:bg-Neutral-3',
              {
                'font-bold !text-white': isSecondButtonHighlighted,
                '!bg-Neutral-3 !text-Neutral-4': isFirstButtonHighlighted,
                '!bg-auxiliaries-3': isYesNoOrUpDown && isSecondButtonHighlighted,
                '!bg-[var(--color-brand-04)]/90': !isYesNoOrUpDown && isSecondButtonHighlighted,
              }
            )}>
            <span
              className={classNames(
                'flex items-center gap-2',
                secondOdds ? 'w-full justify-between text-base leading-none' : 'text-lg font-medium'
              )}>
              {shouldShowUpDownArrows && <ArrowDown width="16" height="16" />}
              {secondOdds ? <span className="font-normal">{secondLabel}</span> : secondLabel}
              {secondOdds && <span className="font-bold tabular-nums">{secondOdds}</span>}
            </span>
          </button>
        </div>
      )

      // Buttons mode (mono binary Yes/No or Up/Down positions)
      if (ui.displayMode === 'buttons') {
        return positionButtons
      }

      // Mono custom: dropdown of virtual outcomes
      if (ui.kind === 'independent_mono_custom') {
        const { actualOutcome, virtualOutcomes } = getVirtualOutcomesForMonoCustom(question)
        if (!actualOutcome || virtualOutcomes.length !== 2) return null

        const outcomeList = virtualOutcomes.map(v => ({
          value: v.virtualId,
          text: (
            <div className="flex gap-2 items-center">
              {v.thumbnail && (
                <Thumbnail
                  image={v.thumbnail}
                  alt={v.title}
                  className="rounded w-8 h-8"
                  variant="rect"
                />
              )}
              <span>{v.title}</span>
            </div>
          ),
        }))

        const selectedValue =
          outcome && outcome.id === actualOutcome.id && position
            ? `${actualOutcome.id}_${position === constants.position.LONG ? 'long' : 'short'}`
            : null

        return (
          <Dropdown
            type="outline"
            dropdownType="list"
            placeholder={i18n('place_bet.select_bet')}
            value={selectedValue}
            list={outcomeList}
            arrow="outlined"
            singleOptionStyle="tick"
            className="market-filter"
            onChange={virtualId => {
              const selectedVirtual = virtualOutcomes.find(v => v.virtualId === virtualId)
              if (!selectedVirtual) return
              onChangeOutcome(actualOutcome)
              setPosition(selectedVirtual.position)
            }}
          />
        )
      }

      // Soccer match (1X2): home / tie / away team buttons instead of the
      // outcome dropdown, mirroring the world-cup lobby bet box
      if (ui.isSportsEvent && !ui.isMono && question.outcomes?.length >= 2) {
        const sportsData = getSportsEventData(question) || {}
        const slots = resolveSportOutcomeSlots(question.outcomes, sportsData)

        if (slots.home && slots.away) {
          const marketCurrencyId =
            currencyId === currencies.OOM.id ? currencies.OOM.id : question.canonical_currency
          const entries = [
            {
              slotOutcome: slots.home,
              flag: getTeamFlagUrl(
                sportsData.home_team_short_code,
                sportsData.home_team_logo || slots.home.thumbnail
              ),
            },
            slots.tie && { slotOutcome: slots.tie, flag: null },
            {
              slotOutcome: slots.away,
              flag: getTeamFlagUrl(
                sportsData.away_team_short_code,
                sportsData.away_team_logo || slots.away.thumbnail
              ),
            },
          ].filter(Boolean)

          return (
            <>
              <div
                className={classNames(
                  'grid w-full gap-2',
                  entries.length === 3 ? 'grid-cols-3' : 'grid-cols-2'
                )}>
                {entries.map(({ slotOutcome, flag }) => {
                  const isSelected = outcome && outcome.id === slotOutcome.id
                  const price = Number(slotOutcome.price?.[marketCurrencyId]) || 0
                  return (
                    <button
                      key={slotOutcome.id}
                      type="button"
                      onClick={() => onChangeOutcome(slotOutcome)}
                      aria-pressed={isSelected}
                      className={classNames(
                        'flex min-w-0 cursor-pointer flex-col items-center gap-1 rounded-[10px] border-2 border-solid px-1 py-3 transition-all duration-200',
                        isSelected
                          ? 'border-[var(--color-brand-04)] bg-[var(--color-brand-04)] shadow-[0_4px_14px_rgba(0,39,204,0.22)]'
                          : 'border-transparent bg-surface-2 shadow-[var(--shadow-raised-soft)] hover:bg-surface-3'
                      )}>
                      <span
                        className={classNames(
                          'flex max-w-full items-center gap-1.5 px-1 text-xs font-semibold uppercase',
                          isSelected ? 'text-[var(--color-contrast-01)]' : 'text-Neutral-5'
                        )}>
                        {flag && (
                          <Image
                            src={flag}
                            alt=""
                            className="h-4 w-4 shrink-0 rounded-full"
                            animate={false}
                          />
                        )}
                        <span className="truncate">
                          {getSportOutcomeLabel(slotOutcome, slots, sportsData)}
                        </span>
                      </span>
                      <span
                        className={classNames(
                          'tnum text-xl font-bold leading-tight',
                          isSelected ? 'text-[var(--color-contrast-01)]' : 'text-[var(--color-brand-04)]'
                        )}>
                        {Math.round(price * 100)}%
                      </span>
                    </button>
                  )
                })}
              </div>
              {positionButtons}
            </>
          )
        }
      }

      // Multi outcome: dropdown. If list-with-buttons, also show long/short buttons below.
      const outcomeList = question.outcomes.map(o => ({
        value: o.id,
        text: (
          <div className="flex gap-2 items-center min-w-0">
            <Thumbnail
              image={o.thumbnail}
              alt={o.title}
              className="rounded w-8 h-8"
              variant="rect"
            />
            <span className="truncate">{o.title}</span>
          </div>
        ),
      }))

      return (
        <>
          <Dropdown
            type="outline"
            dropdownType="list"
            placeholder={i18n('place_bet.select_bet')}
            value={outcome && outcome.id}
            list={outcomeList}
            arrow="outlined"
            singleOptionStyle="tick"
            className="market-filter"
            onChange={id => {
              onChangeOutcome(question.outcomes.find(o => o.id === id))
            }}
          />
          {ui.displayMode === 'list-with-buttons' && positionButtons}
        </>
      )
    }

    return (
      <div className="flex flex-col w-full rounded-[var(--radius-sm)] ">
        <div className="space-y-3 !p-5" ref={animationParent}>
          {renderOutcomeSelector()}
          {marketType === 'limit' && (
            <>
              {renderLimitInputs()}

              {(errorId === 'insufficient_funds' || errorId === 'amount_below_min') && (
                <div className="mt-1 text-negative-4 text-xs">
                  {errorTypes[errorId]}
                </div>
              )}

              {renderExpirationRow()}
            </>
          )}
          {marketType === 'market' && renderMarketInput()}
        </div>
        <div className="flex flex-col gap-3 !px-5 !pb-4">
          {marketType === 'limit' && renderCostInfo()}
          {renderBetInfo()}
          {renderSubmitButton()}
        </div>

        <CalendarSheet
          isOpen={calendarOpen}
          onClose={() => setCalendarOpen(false)}
          title={i18n('place_bet.select_expiration_date')}
          modalProps={{ position: 'center' }}>
          {renderCalendarContent()}
        </CalendarSheet>
      </div>
    )
  }
)

export default BuyPlaceBet
